import { Component, Host, h, Element } from '@stencil/core';

export interface AhaLink extends HTMLAhaLinkElement {}

/**
 * Link component that performs XSS Sanitization on href. It uses [Angular url_sanitizer](https://github.com/angular/angular/blob/master/packages/core/src/sanitization/url_sanitizer.ts)
 * to ensure hrefs in anchors are safe. If they're not it would append `unsafe:` to the href so browsers won't trigger any JS. It also adds `rel="noopener noreferrer"` when `target="_blank"`.
 * @slot default - Contents of the default slot.
 */
@Component({
  tag: 'aha-link',
  shadow: true,
})
export class AhaLink {
  @Element() host!: HTMLAhaLinkElement;

  private renderOnlyFirstChild() {
    Array.from(this.host.children)
      .slice(1)
      .forEach(item => item.remove());
  }

  private SAFE_URL_PATTERN = /^(?:(?:https?|mailto|ftp|tel|file|sms):|[^&:/?#]*(?:[/?#]|$))/gi;

  /** A pattern that matches safe data URLs. It only matches image, video, and audio types. */
  private DATA_URL_PATTERN =
    /^data:(?:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp)|video\/(?:mpeg|mp4|ogg|webm)|audio\/(?:mp3|oga|ogg|opus));base64,[a-z0-9+\/]+=*$/i;

  private _sanitizeUrl(url: string): string {
    url = String(url);
    if (url === 'null' || url.length === 0 || url === 'about:blank') return 'about:blank';
    if (url.match(this.SAFE_URL_PATTERN) || url.match(this.DATA_URL_PATTERN)) return url;

    return `unsafe:${url}`;
  }

  private sanitizeAnchor() {
    // Make sure only the first child is rendered.
    this.renderOnlyFirstChild();
    const children = this.host.children;
    if (children.length === 0) return;
    // Sanitize anchor href
    const anchor = this.host.querySelector('a');
    if (!anchor) {
      this.host.children[0].remove();
      return console.error('aha-link: invalid or missing anchor element.');
    }
    const href = anchor.getAttribute('href');
    anchor.setAttribute('href', this._sanitizeUrl(String(href).trim()));
    // Add rel="noopener noreferrer" when target="_blank"
    const target = anchor.getAttribute('target');
    if (target && target === '_blank') {
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  }

  render() {
    return (
      <Host>
        <slot onSlotchange={() => this.sanitizeAnchor()}></slot>
      </Host>
    );
  }
}
